import { Logo } from "@/components/brand";
import { UserAvatar } from "@/components/avatar";
import { TeamIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { post } from "@/lib/api";
import { useMe, useMeId, useMyTeams, useTeams, useUnreadCount, useUsers } from "@/lib/hooks";
import { dropTargetProps, targetKey, useSidebarDrop, type DropTarget } from "@/lib/sidebar-drop";
import { shortcutLabel } from "@/lib/hotkeys";
import { usePeers } from "@/lib/presence";
import { useRows, useTable } from "@/lib/store";
import { sync } from "@/lib/sync";
import type { Team } from "@/lib/types";
import { openCommand, openCreateIssue, readPref, setMobileSidebar, setShortcutsOpen, setTheme, shownTheme, useTheme, writePref, type Theme } from "@/lib/ui";
import { cn } from "@/lib/utils";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  Archive,
  BookOpen,
  Building2,
  ChartColumn,
  ChevronDown,
  CircleHelp,
  Ellipsis,
  CircleUserRound,
  FilePen,
  Hexagon,
  House,
  Inbox,
  Layers,
  LayoutDashboard,
  Lightbulb,
  ListTodo,
  LogOut,
  Map,
  Moon,
  Plus,
  Radio,
  RefreshCcw,
  Search,
  Settings,
  SquarePen,
  StickyNote,
  Sun,
  Target,
  Users,
  Workflow,
} from "lucide-react";
import { useState, useSyncExternalStore, type ReactNode } from "react";
import { createPortal } from "react-dom";

/** Outline for sidebar drop targets while an issue is being dragged. */
function dropClass(target: DropTarget | undefined, drop: { dragging: boolean; hover: string | null }) {
  if (!target || !drop.dragging) return undefined;
  return drop.hover === targetKey(target) ? "bg-brand/15 text-foreground outline outline-1 outline-brand" : "outline outline-1 outline-dashed outline-border";
}

function NavItem({ to, params, icon, label, badge, exact, drop, indent }: { to: string; params?: Record<string, string>; icon: ReactNode; label: string; badge?: ReactNode; exact?: boolean; drop?: DropTarget; indent?: boolean }) {
  const dropState = useSidebarDrop();
  return (
    <Link
      to={to as never}
      params={params as never}
      activeOptions={{ exact: exact ?? false }}
      onClick={() => setMobileSidebar(false)}
      {...(drop ? dropTargetProps(drop) : {})}
      className={cn(
        "group/nav flex h-7 items-center gap-2 rounded-md px-2 text-[13px] text-sidebar-foreground transition-colors duration-150 pointer-coarse:h-10 pointer-coarse:gap-3 pointer-coarse:text-[15px] hover:bg-sidebar-accent/70 data-[status=active]:bg-sidebar-accent data-[status=active]:font-medium data-[status=active]:text-sidebar-accent-foreground",
        indent && "pl-7 pointer-coarse:pl-9",
        dropClass(drop, dropState),
      )}
    >
      <span className="flex size-4 items-center justify-center text-muted-foreground group-hover/nav:text-sidebar-foreground group-data-[status=active]/nav:text-sidebar-accent-foreground pointer-coarse:size-5 [&_svg]:size-4 [&_svg]:stroke-[1.75] pointer-coarse:[&_svg]:size-[18px]">{icon}</span>
      <span className="flex-1 truncate">{label}</span>
      {badge}
    </Link>
  );
}

/** Open or closed, remembered per section. */
function useOpen(key: string, initial = true) {
  const [open, setOpen] = useState(() => readPref(`sidebar.${key}`, initial));
  return [open, (v: boolean) => (setOpen(v), writePref(`sidebar.${key}`, v))] as const;
}

function Section({ id, title, children, action }: { id: string; title: string; children: ReactNode; action?: ReactNode }) {
  const [open, setOpen] = useOpen(id);
  return (
    <div className="mt-5">
      <div className="group/sec flex h-6 items-center pr-1 pl-2">
        <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-1 rounded text-[12px] font-medium text-muted-foreground hover:text-sidebar-foreground" aria-expanded={open}>
          {title}
          <ChevronDown className={cn("size-3 transition-transform", !open && "-rotate-90")} />
        </button>
        <span className="flex-1" />
        {action}
      </div>
      {open && <div className="mt-0.5 flex flex-col gap-px">{children}</div>}
    </div>
  );
}

const MORE_ROUTES = ["/customers", "/customer/", "/docs", "/doc/", "/insights", "/dashboards", "/dashboard/", "/planning", "/workload", "/archive"];

/** Less-used workspace pages fold under More, and stay open while one of them is showing. */
function MoreItems() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const here = MORE_ROUTES.some((r) => pathname.startsWith(r));
  const [open, setOpen] = useOpen("more", false);
  return (
    <>
      {(open || here) && (
        <>
          <NavItem to="/customers" icon={<Building2 />} label="Customers" />
          <NavItem to="/docs" icon={<BookOpen />} label="Documents" />
          <NavItem to="/insights" icon={<ChartColumn />} label="Insights" />
          <NavItem to="/dashboards" icon={<LayoutDashboard />} label="Dashboards" />
          <NavItem to="/planning" icon={<Workflow />} label="Planning" />
          <NavItem to="/workload" icon={<Users />} label="Workload" />
          <NavItem to="/archive" icon={<Archive />} label="Archive" />
        </>
      )}
      {!here && (
        <button type="button" onClick={() => setOpen(!open)} className="flex h-7 items-center gap-2 rounded-md px-2 text-[13px] text-sidebar-foreground hover:bg-sidebar-accent/70 pointer-coarse:h-10 pointer-coarse:gap-3 pointer-coarse:text-[15px]" aria-expanded={open}>
          <span className="flex size-4 items-center justify-center text-muted-foreground [&_svg]:size-4">
            <Ellipsis />
          </span>
          {open ? "Less" : "More"}
        </button>
      )}
    </>
  );
}

function TeamNav({ team }: { team: Team }) {
  const [open, setOpen] = useOpen(`team.${team.id}`);
  const triage = useRows("issue", (i) => i.teamId === team.id, undefined, [team.id]);
  const states = useTable("workflowState");
  const triageCount = triage.filter((i) => states[i.stateId]?.category === "triage" && (!i.snoozedUntil || i.snoozedUntil < new Date().toISOString())).length;
  return (
    <div>
      <button type="button" onClick={() => setOpen(!open)} className="flex h-7 w-full items-center gap-2 rounded-md px-2 text-[13px] text-sidebar-foreground hover:bg-sidebar-accent/70 pointer-coarse:h-10 pointer-coarse:gap-3 pointer-coarse:text-[15px]" aria-expanded={open}>
        <TeamIcon teamId={team.id} className="size-4 rounded-[4px] text-[8px]" />
        <span className="truncate text-left">{team.name}</span>
        {team.private && <span className="text-[10.5px] text-muted-foreground">Private</span>}
        <ChevronDown className={cn("size-3 shrink-0 text-muted-foreground transition-transform", !open && "-rotate-90")} />
      </button>
      {open && (
        <div className="flex flex-col gap-px">
          <NavItem indent to="/team/$key/issues" params={{ key: team.key }} icon={<ListTodo />} label="Issues" />
          {team.triageEnabled && <NavItem indent to="/team/$key/triage" params={{ key: team.key }} icon={<Inbox />} label="Triage" badge={triageCount > 0 ? <span className="text-[11.5px] text-muted-foreground tabular">{triageCount}</span> : null} />}
          {team.cyclesEnabled && <NavItem indent to="/team/$key/cycles" params={{ key: team.key }} icon={<RefreshCcw />} label="Cycles" drop={{ kind: "cycle", teamId: team.id, label: team.name }} />}
        </div>
      )}
    </div>
  );
}

function SyncDot() {
  const snap = useSyncExternalStore(sync.subscribe, sync.getSnapshot);
  const peers = usePeers();
  const online = new Set(peers.map((p) => p.userId)).size;
  const label = snap.status === "live" ? (snap.pending ? `Saving ${snap.pending} change${snap.pending > 1 ? "s" : ""}…` : "Live") : snap.status === "connecting" ? "Connecting…" : snap.pending ? `Offline. ${snap.pending} changes waiting` : "Offline. Changes will sync when you're back";
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2 px-2 text-[12px] text-muted-foreground" title={`Change #${snap.lastSeq}`}>
      <span className={cn("size-1.5 shrink-0 rounded-full", snap.status === "live" ? "bg-emerald-500" : snap.status === "connecting" ? "animate-soft-pulse bg-amber-500" : "bg-destructive")} />
      <span className="flex-1 truncate">{label}</span>
      {online > 0 && (
        <span className="flex items-center gap-1" title={`${online} others online`}>
          <Radio className="size-3" /> {online}
        </span>
      )}
    </div>
  );
}

function Favorites() {
  const favs = useRows("favorite", undefined, (a, b) => (a.sortKey < b.sortKey ? -1 : 1));
  const projects = useTable("project");
  const views = useTable("view");
  const docs = useTable("document");
  const ideas = useTable("idea");
  const initiatives = useTable("initiative");
  const dashboards = useTable("dashboard");
  const customers = useTable("customer");
  const issues = useTable("issue");
  const teams = useTable("team");
  if (!favs.length) return null;
  return (
    <Section id="favorites" title="Favorites">
      {favs.map((f) => {
        switch (f.entity) {
          case "project":
            return projects[f.entityId] && <NavItem key={f.id} to="/project/$id" params={{ id: f.entityId }} icon={<Hexagon style={{ color: projects[f.entityId].color }} />} label={projects[f.entityId].name} drop={{ kind: "project", id: f.entityId, label: projects[f.entityId].name }} />;
          case "view":
            return views[f.entityId] && <NavItem key={f.id} to="/view/$id" params={{ id: f.entityId }} icon={<Layers />} label={views[f.entityId].name} />;
          case "document":
            return docs[f.entityId] && <NavItem key={f.id} to="/doc/$id" params={{ id: f.entityId }} icon={<BookOpen />} label={docs[f.entityId].title} />;
          case "idea":
            return ideas[f.entityId] && <NavItem key={f.id} to="/idea/$id" params={{ id: f.entityId }} icon={<Lightbulb />} label={ideas[f.entityId].title} />;
          case "initiative":
            return initiatives[f.entityId] && <NavItem key={f.id} to="/initiative/$id" params={{ id: f.entityId }} icon={<Target />} label={initiatives[f.entityId].name} />;
          case "dashboard":
            return dashboards[f.entityId] && <NavItem key={f.id} to="/dashboard/$id" params={{ id: f.entityId }} icon={<LayoutDashboard />} label={dashboards[f.entityId].name} />;
          case "customer":
            return customers[f.entityId] && <NavItem key={f.id} to="/customer/$id" params={{ id: f.entityId }} icon={<Building2 />} label={customers[f.entityId].name} />;
          case "issue": {
            const i = issues[f.entityId];
            if (!i) return null;
            const ident = `${teams[i.teamId]?.key}-${i.number}`;
            return <NavItem key={f.id} to="/issue/$ident" params={{ ident }} icon={<CircleUserRound />} label={i.title} />;
          }
          default:
            return null;
        }
      })}
    </Section>
  );
}

/**
 * Shown only while an issue is dragged: drop on a person to assign it. It
 * floats at the bottom of the window so it never covers sidebar targets.
 */
function AssignDock() {
  const drop = useSidebarDrop();
  const users = useUsers();
  const me = useMeId();
  if (!drop.dragging) return null;
  const people = [...users.filter((u) => u.id === me), ...users.filter((u) => u.id !== me && u.role !== "guest")];
  return createPortal(
    <div className="fixed bottom-4 left-1/2 z-50 flex max-w-[min(720px,92vw)] -translate-x-1/2 flex-wrap items-center gap-1 rounded-xl border border-border bg-popover p-1.5 text-popover-foreground shadow-lg">
      <span className="px-1.5 text-[11.5px] font-medium text-muted-foreground">Assign to</span>
      {people.map((u) => {
        const target: DropTarget = { kind: "assignee", id: u.id, label: u.id === me ? "you" : u.name };
        return (
          <div key={u.id} {...dropTargetProps(target)} className={cn("flex h-7 items-center gap-1.5 rounded-md px-2 text-[13px]", dropClass(target, drop))}>
            <UserAvatar userId={u.id} size="xs" />
            <span className="max-w-32 truncate">{u.id === me ? "Me" : u.name.split(" ")[0]}</span>
          </div>
        );
      })}
    </div>,
    document.body,
  );
}

function WorkspaceMenu() {
  const me = useMe().data;
  const theme = useTheme();
  const ws = me?.membership?.workspace;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="flex h-8 min-w-0 items-center gap-2 rounded-md px-1.5 hover:bg-sidebar-accent/70 aria-expanded:bg-sidebar-accent/70">
          <Logo className="size-[18px] rounded-[5px]" />
          <span className="truncate text-[13px] font-semibold">{ws?.name ?? "Dayax"}</span>
          <ChevronDown className="size-3 shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-60">
        <DropdownMenuLabel className="flex items-center gap-2 font-normal">
          <UserAvatar userId={me?.user.id} size="md" />
          <span className="min-w-0">
            <span className="block truncate text-[13px] font-medium">{me?.user.name}</span>
            <span className="block truncate text-xs text-muted-foreground">{me?.user.email}</span>
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/settings/$section" params={{ section: "profile" }}>
            <Settings /> Settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/settings/$section" params={{ section: "members" }}>
            <Users /> Invite and manage members
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>Theme</DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup value={theme} onValueChange={(v) => setTheme(v as Theme)}>
              <DropdownMenuRadioItem value="system">Match system</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="light">Light</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="dark">Dark</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuItem onSelect={() => setShortcutsOpen(true)}>
          <CircleHelp /> Keyboard shortcuts
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={async () => {
            await post("/api/auth/logout");
            sync.stop();
            location.assign("/login");
          }}
        >
          <LogOut /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Flips light and dark; the new theme spreads out from the button. */
function ThemeSwitch() {
  useTheme();
  const dark = shownTheme() === "dark";
  return (
    <Button
      variant="ghost"
      size="icon-xs"
      className="rounded-full text-muted-foreground hover:bg-sidebar-accent/70"
      onClick={(e) => setTheme(dark ? "light" : "dark", { x: e.clientX, y: e.clientY })}
      aria-label={dark ? "Switch to light theme" : "Switch to dark theme"}
      title={dark ? "Light theme" : "Dark theme"}
    >
      {dark ? <Sun className="size-3.5" /> : <Moon className="size-3.5" />}
    </Button>
  );
}

export function Sidebar() {
  const unread = useUnreadCount();
  const me = useMeId();
  const drafts = useRows("issueDraft", (d) => d.userId === me, undefined, [me]).length;
  const myTeams = useMyTeams();
  const teams = useTeams();
  const others = teams.filter((t) => !myTeams.includes(t));
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return (
    <nav className="relative flex h-full w-full flex-col overflow-hidden bg-sidebar text-sidebar-foreground" aria-label="Main">
      <div aria-hidden className="pointer-events-none absolute -top-28 -left-20 size-64 rounded-full opacity-50 dark:opacity-100" style={{ background: "radial-gradient(closest-side, color-mix(in oklch, var(--moon) 9%, transparent), transparent)" }} />
      <div className="flex items-center gap-0.5 pt-2 pr-2 pl-2.5">
        <WorkspaceMenu />
        <span className="flex-1" />
        <Button variant="ghost" size="icon-sm" className="text-muted-foreground hover:bg-sidebar-accent/70" onClick={() => openCommand()} aria-label={`Search (${shortcutLabel("mod+k")})`} title={`Search ${shortcutLabel("mod+k")}`}>
          <Search />
        </Button>
        <Button variant="outline" size="icon-sm" className="bg-background shadow-xs dark:bg-sidebar-accent" onClick={() => openCreateIssue()} aria-label="New issue (C)" title="New issue (C)">
          <SquarePen />
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto px-2.5 pt-3 pb-4" key={pathname.split("/")[1]}>
        <div className="flex flex-col gap-px">
          <NavItem to="/" exact icon={<House />} label="Home" />
          <NavItem to="/inbox" icon={<Inbox />} label="Inbox" badge={unread > 0 ? <span className="min-w-4 rounded-full bg-brand px-1 text-center text-[10.5px] leading-4 font-semibold text-brand-foreground tabular">{unread}</span> : null} />
          <NavItem to="/my-issues" icon={<CircleUserRound />} label="My work" drop={me ? { kind: "assignee", id: me, label: "you" } : undefined} />
          <NavItem to="/drafts" icon={<FilePen />} label="Drafts" badge={drafts > 0 ? <span className="text-[11.5px] text-muted-foreground tabular">{drafts}</span> : null} />
          <NavItem to="/stickies" icon={<StickyNote />} label="Stickies" />
        </div>
        <Section id="workspace" title="Workspace">
          <NavItem to="/projects" icon={<Hexagon />} label="Projects" />
          <NavItem to="/initiatives" icon={<Target />} label="Initiatives" />
          <NavItem to="/roadmap" icon={<Map />} label="Roadmap" />
          <NavItem to="/ideas" icon={<Lightbulb />} label="Idea shelf" />
          <NavItem to="/pulse" icon={<Radio />} label="Pulse" />
          <NavItem to="/views" icon={<Layers />} label="Views" />
          <MoreItems />
        </Section>
        <Favorites />
        <Section
          id="teams"
          title="Your teams"
          action={
            <Link to="/settings/$section" params={{ section: "teams" }} className="rounded p-0.5 text-muted-foreground opacity-0 group-hover/sec:opacity-100 hover:bg-sidebar-accent hover:text-foreground focus-visible:opacity-100" aria-label="Add a team">
              <Plus className="size-3.5" />
            </Link>
          }
        >
          {myTeams.map((t) => (
            <TeamNav key={t.id} team={t} />
          ))}
        </Section>
        {others.length > 0 && (
          <Section id="other-teams" title="Other teams">
            {others.map((t) => (
              <TeamNav key={t.id} team={t} />
            ))}
          </Section>
        )}
      </div>
      <div className="flex items-center gap-1 px-2.5 pt-1 pb-2.5">
        <AssignDock />
        <SyncDot />
        <ThemeSwitch />
        <Button variant="ghost" size="icon-xs" className="rounded-full text-muted-foreground hover:bg-sidebar-accent/70" onClick={() => setShortcutsOpen(true)} aria-label="Keyboard shortcuts" title="Keyboard shortcuts">
          <CircleHelp className="size-3.5" />
        </Button>
      </div>
    </nav>
  );
}
